import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight, Menu, X, Mic, Star, Heart, Hand, Calendar, Clock,
  ShieldCheck, Lock, Trash2, Sparkles, UserRound, Wand2, Compass
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ZodiacMandala, TwinkleField } from '../components/shubh/ZodiacMandala';

const CTA_IMAGE = 'https://images.unsplash.com/photo-1763610642272-787bfeb8045c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const Counter = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let cur = 0;
    const step = value / 50;
    const t = setInterval(() => {
      cur += step;
      if (cur >= value) { setN(value); clearInterval(t); } else setN(Math.floor(cur));
    }, 30);
    return () => clearInterval(t);
  }, [inView, value]);
  return <span ref={ref}>{n.toLocaleString('en-IN')}{suffix}</span>;
};

const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C84B32] mb-4">{children}</p>
);

const LandingNav = ({ hi, onStart, toggleLanguage }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const links = [
    { label: hi ? 'सुविधाएं' : 'Features', href: '#features' },
    { label: hi ? 'कैसे काम करता है' : 'How it works', href: '#how-it-works' },
    { label: hi ? 'विश्वास' : 'Trust', href: '#trust' },
  ];
  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled ? 'bg-[#FFF9EF]/85 backdrop-blur-xl shadow-[0_1px_0_rgba(40,38,34,0.08)]' : ''}`} data-testid="landing-nav">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" data-testid="landing-brand">
          <span className="w-9 h-9 rounded-full flex items-center justify-center text-white font-['Fraunces'] text-lg" style={{ background: 'linear-gradient(135deg, #F5B942, #C84B32)' }}>ॐ</span>
          <span className="font-['Fraunces'] text-xl text-[#282622]">Nakshatra</span>
        </a>
        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm font-medium text-[#6B6560] hover:text-[#C84B32] transition-colors" data-testid={`landing-nav-${l.href.slice(1)}`}>{l.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleLanguage} className="px-3 py-1.5 rounded-full text-sm font-semibold text-[#282622] bg-white/70 border border-[#282622]/10 hover:border-[#F5B942] transition-colors" data-testid="landing-language-toggle">
            {hi ? 'EN' : 'हि'}
          </button>
          <button onClick={onStart} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-[0_8px_20px_rgba(200,75,50,0.3)] hover:-translate-y-0.5 transition-transform" style={{ background: 'linear-gradient(135deg, #C84B32, #E85D43)' }} data-testid="landing-nav-cta">
            {hi ? 'शुरू करें' : 'Begin Journey'} <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen((v) => !v)} className="md:hidden p-2 text-[#282622]" aria-label="Toggle menu" data-testid="landing-mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden overflow-hidden bg-[#FFF9EF] border-t border-[#282622]/10">
            <div className="px-6 py-5 space-y-3">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block py-2 text-[#282622] font-medium">{l.label}</a>
              ))}
              <button onClick={onStart} className="w-full py-3 rounded-full font-semibold text-white" style={{ background: 'linear-gradient(135deg, #C84B32, #E85D43)' }} data-testid="landing-mobile-cta">
                {hi ? 'शुरू करें' : 'Begin Journey'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const GuruBubble = ({ hi }) => {
  const msgs = hi
    ? ['क्या इस साल मेरी नौकरी बदलेगी?', 'बृहस्पति आपके दशम भाव में गोचर कर रहे हैं — अक्टूबर के बाद नए अवसर प्रबल हैं।']
    : ['Will my career change this year?', 'Jupiter is transiting your 10th house — strong new openings after October.'];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % 3), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.div {...reveal(0.6)} className="absolute -bottom-6 -left-4 sm:left-0 w-[270px] rounded-3xl bg-white/90 backdrop-blur-xl border border-white shadow-[0_20px_50px_rgba(82,58,26,0.16)] p-4" data-testid="landing-guru-bubble">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #F5B942, #C84B32)' }}><Mic className="w-4 h-4" /></span>
        <div>
          <p className="text-sm font-semibold text-[#282622]">{hi ? 'वैदिक गुरु' : 'Vedic Guru'}</p>
          <p className="text-[11px] text-[#287A60] flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#287A60] animate-pulse" />{hi ? 'सुन रहे हैं' : 'Listening'}</p>
        </div>
      </div>
      <div className="space-y-2 min-h-[92px]">
        <AnimatePresence>
          {step >= 0 && (
            <motion.p key="q" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ml-auto max-w-[85%] w-fit text-xs px-3 py-2 rounded-2xl rounded-br-md text-white" style={{ background: '#C84B32' }}>{msgs[0]}</motion.p>
          )}
          {step >= 1 && (
            <motion.p key="a" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-[92%] text-xs px-3 py-2 rounded-2xl rounded-bl-md bg-[#FCE9D9] text-[#282622]">{msgs[1]}</motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const Hero = ({ hi, onStart, navigate }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, -60]);
  return (
    <section id="top" className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 px-5 sm:px-8 overflow-hidden">
      <TwinkleField />
      <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(246,199,211,0.5), transparent 65%)' }} />
      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <motion.div style={{ y }}>
          <motion.div {...reveal(0.05)}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-[#F5B942]/50 text-xs font-semibold text-[#B7791F] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> {hi ? 'भारतीय वैदिक ज्योतिष, AI के साथ' : 'Indian Vedic Jyotish, powered by AI'}
            </span>
          </motion.div>
          <motion.h1 {...reveal(0.12)} className="font-['Fraunces'] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#282622] mb-6">
            {hi ? <>तारों में लिखी है<br /><span className="italic text-[#C84B32]">आपकी कहानी</span></> : <>The stars know<br /><span className="italic text-[#C84B32]">your story</span></>}
          </motion.h1>
          <motion.p {...reveal(0.2)} className="text-base sm:text-lg text-[#6B6560] max-w-lg mb-10 leading-relaxed">
            {hi
              ? 'सटीक जन्म कुंडली, दैनिक राशिफल, गुण मिलान और बोलकर पूछने वाले वैदिक गुरु — आपके जीवन के हर मोड़ के लिए।'
              : 'Precise Janam Kundli, daily Rashifal, Guna Milan and a Vedic Guru you can simply talk to — guidance for every turn of life.'}
          </motion.p>
          <motion.div {...reveal(0.28)} className="flex flex-col sm:flex-row gap-3">
            <button onClick={onStart} className="shubh-shimmer group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white font-semibold shadow-[0_14px_34px_rgba(200,75,50,0.35)] hover:-translate-y-0.5 transition-transform" style={{ background: 'linear-gradient(135deg, #C84B32 0%, #E85D43 60%, #E8913A 100%)' }} data-testid="hero-cta-btn">
              {hi ? 'अपना भविष्य जानें' : 'Reveal My Destiny'} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/login')} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-[#282622] bg-white border border-[#282622]/10 hover:border-[#F5B942] transition-colors" data-testid="hero-signin-btn">
              {hi ? 'लॉगिन करें' : 'I have an account'}
            </button>
          </motion.div>
          <motion.p {...reveal(0.34)} className="text-sm text-[#8A8279] mt-6">{hi ? 'मुफ्त शुरुआत • हिंदी और English' : 'Start free • Hindi & English'}</motion.p>
        </motion.div>
        <div className="relative mx-auto w-[300px] sm:w-[420px] lg:w-[480px]">
          <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -20 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}>
            <ZodiacMandala className="w-full" showGlyphs={false} />
          </motion.div>
          <GuruBubble hi={hi} />
          <motion.div {...reveal(0.8)} className="absolute -top-2 -right-2 sm:right-4 rounded-2xl bg-white/90 backdrop-blur border border-white shadow-lg px-4 py-3" data-testid="landing-badge-nakshatra">
            <p className="font-['Fraunces'] text-2xl text-[#C84B32] leading-none">27</p>
            <p className="text-[11px] text-[#6B6560] mt-1">{hi ? 'नक्षत्र' : 'Nakshatras'}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Marquee = ({ hi }) => {
  const words = hi
    ? ['जन्म कुंडली', 'गुण मिलान', 'विंशोत्तरी दशा', 'पंचांग', 'हस्तरेखा', 'वैदिक गुरु', 'शुभ मुहूर्त', 'नवांश']
    : ['Janam Kundli', 'Guna Milan', 'Vimshottari Dasha', 'Panchang', 'Palm Reading', 'Vedic Guru', 'Shubh Muhurat', 'Navamsa'];
  const row = [...words, ...words];
  return (
    <div className="relative py-5 border-y border-[#282622]/10 bg-white/50 overflow-hidden" data-testid="landing-marquee">
      <motion.div className="flex gap-10 whitespace-nowrap w-max" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}>
        {row.map((w, i) => (
          <span key={i} className="flex items-center gap-10 font-['Fraunces'] text-xl sm:text-2xl text-[#282622]/80">
            {w}<Star className="w-4 h-4 text-[#F5B942] fill-[#F5B942]" />
          </span>
        ))}
      </motion.div>
    </div>
  );
};

const Stats = ({ hi }) => (
  <section className="py-16 px-5 sm:px-8">
    <div className="max-w-5xl mx-auto grid grid-cols-3 gap-6">
      {[
        { v: 50000, s: '+', l: hi ? 'विश्वासी उपयोगकर्ता' : 'Trusted seekers' },
        { v: 100000, s: '+', l: hi ? 'भविष्यवाणियां' : 'Readings delivered' },
        { v: 12, s: '', l: hi ? 'वैदिक उपकरण' : 'Vedic tools' },
      ].map((st, i) => (
        <motion.div key={st.l} {...reveal(i * 0.08)} className="text-center">
          <p className="font-['Fraunces'] text-3xl sm:text-5xl text-[#C84B32]"><Counter value={st.v} suffix={st.s} /></p>
          <p className="text-xs sm:text-sm text-[#6B6560] mt-2">{st.l}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const FeatureCard = ({ f, i, onStart }) => (
  <motion.button
    {...reveal(i * 0.06)}
    whileHover={{ y: -6 }}
    onClick={onStart}
    className={`group relative flex flex-col items-start text-left rounded-[28px] p-7 border overflow-hidden ${f.span || ''} ${f.dark ? 'text-white border-transparent' : 'bg-white border-[#282622]/10 text-[#282622] shadow-[0_2px_12px_rgba(82,58,26,0.06)] hover:shadow-[0_18px_40px_rgba(82,58,26,0.12)]'} transition-shadow`}
    style={f.dark ? { background: 'linear-gradient(140deg, #C84B32 0%, #E8913A 100%)' } : undefined}
    data-testid={`landing-feature-${f.id}`}
  >
    <span className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${f.dark ? 'bg-white/20' : ''}`} style={f.dark ? undefined : { background: f.tint }}>
      <f.icon className="w-6 h-6" style={{ color: f.dark ? '#FFFFFF' : f.fg }} />
    </span>
    <h3 className={`font-['Fraunces'] text-2xl mb-2 ${f.dark ? 'text-white' : 'text-[#282622]'}`}>{f.title}</h3>
    <p className={`text-sm leading-relaxed max-w-sm ${f.dark ? 'text-white/85' : 'text-[#6B6560]'}`}>{f.desc}</p>
    <span className={`mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${f.dark ? 'text-white' : 'text-[#C84B32]'}`}>
      {f.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </span>
    {f.dark && <Mic className="absolute -right-6 -bottom-6 w-40 h-40 text-white/10" />}
  </motion.button>
);

const Features = ({ hi, onStart }) => {
  const list = [
    { id: 'guru', icon: Mic, dark: true, span: 'md:col-span-2 md:row-span-2', title: hi ? 'वैदिक गुरु — बस बोलिए' : 'Vedic Guru — just speak', desc: hi ? 'बटन दबाइए, अपना सवाल पूछिए और गुरु जी आपकी कुंडली के आधार पर हिंदी या English में आवाज़ में जवाब देंगे।' : 'Hold the button, ask your question, and Guru Ji answers out loud in Hindi or English — grounded in your own Kundli.', cta: hi ? 'गुरु से पूछें' : 'Ask the Guru' },
    { id: 'kundli', icon: Star, tint: 'rgba(245,185,66,0.18)', fg: '#B7791F', title: hi ? 'जन्म कुंडली' : 'Janam Kundli', desc: hi ? 'D1, D9, D10 चार्ट, ग्रह स्थिति और दशा।' : 'D1, D9 & D10 charts, planets and Dasha timeline.', cta: hi ? 'देखें' : 'View chart' },
    { id: 'milan', icon: Heart, tint: 'rgba(246,199,211,0.5)', fg: '#B5476A', title: hi ? 'कुंडली मिलान' : 'Kundli Milan', desc: hi ? '36 गुणों का अष्टकूट मिलान।' : 'Ashtakoota 36-guna matching for couples.', cta: hi ? 'मिलान करें' : 'Match now' },
    { id: 'palm', icon: Hand, tint: 'rgba(40,122,96,0.12)', fg: '#287A60', title: hi ? 'हस्तरेखा' : 'Palm Reading', desc: hi ? 'हथेली की फोटो से AI विश्लेषण।' : 'AI reads the lines of your palm from a photo.', cta: hi ? 'स्कैन करें' : 'Scan palm' },
    { id: 'panchang', icon: Calendar, tint: 'rgba(118,183,242,0.18)', fg: '#2F6FB0', title: hi ? 'हिंदू पंचांग' : 'Hindu Panchang', desc: hi ? 'तिथि, व्रत, त्योहार और रिमाइंडर।' : 'Tithi, vrat, festivals and reminders.', cta: hi ? 'खोलें' : 'Open' },
    { id: 'timeline', icon: Clock, tint: 'rgba(200,75,50,0.1)', fg: '#C84B32', span: 'md:col-span-4', title: hi ? 'जीवन समयरेखा' : 'Life Timeline', desc: hi ? 'आने वाले शुभ समय, गोचर और महत्वपूर्ण पड़ाव — एक नज़र में।' : 'Upcoming auspicious windows, transits and life milestones — at a glance.', cta: hi ? 'देखें' : 'See timeline' },
  ];
  return (
    <section id="features" className="py-24 px-5 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <motion.div {...reveal()} className="max-w-2xl mb-14">
          <Eyebrow>{hi ? 'आपके उत्तर' : 'Find your answers'}</Eyebrow>
          <h2 className="font-['Fraunces'] text-4xl sm:text-5xl text-[#282622] leading-tight">{hi ? 'जीवन के हर सवाल के लिए एक वैदिक उपकरण' : "A Vedic tool for every question life asks"}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[minmax(220px,auto)] gap-5">
          {list.map((f, i) => <FeatureCard key={f.id} f={f} i={i} onStart={onStart} />)}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = ({ hi }) => (
  <section id="how-it-works" className="py-24 px-5 sm:px-8 bg-white/60">
    <div className="max-w-6xl mx-auto">
      <motion.div {...reveal()} className="text-center mb-16">
        <Eyebrow>{hi ? 'सरल प्रक्रिया' : 'Simple process'}</Eyebrow>
        <h2 className="font-['Fraunces'] text-4xl sm:text-5xl text-[#282622]">{hi ? 'तीन आसान कदम' : 'Three gentle steps'}</h2>
      </motion.div>
      <div className="relative grid md:grid-cols-3 gap-10">
        <div className="hidden md:block absolute top-10 left-[16%] right-[16%] border-t-2 border-dashed border-[#F5B942]/60" />
        {[
          { icon: UserRound, t: hi ? 'जन्म विवरण दें' : 'Share birth details', d: hi ? 'तिथि, समय और स्थान — बस इतना।' : 'Date, time and place — that is all.' },
          { icon: Wand2, t: hi ? 'वैदिक गणना' : 'Vedic calculation', d: hi ? 'प्राचीन सूत्र + आधुनिक AI व्याख्या।' : 'Classical formulas, explained by modern AI.' },
          { icon: Compass, t: hi ? 'मार्गदर्शन पाएं' : 'Receive guidance', d: hi ? 'व्यक्तिगत भविष्यवाणी और उपाय।' : 'Personal predictions and remedies.' },
        ].map((s, i) => (
          <motion.div key={s.t} {...reveal(i * 0.12)} className="relative text-center">
            <div className="relative w-20 h-20 mx-auto mb-6 rounded-full bg-white border border-[#F5B942]/50 shadow-[0_10px_30px_rgba(245,185,66,0.25)] flex items-center justify-center">
              <s.icon className="w-8 h-8 text-[#C84B32]" />
              <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: '#C84B32' }}>{i + 1}</span>
            </div>
            <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-2">{s.t}</h3>
            <p className="text-sm text-[#6B6560]">{s.d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = ({ hi }) => (
  <section id="trust" className="py-24 px-5 sm:px-8">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <motion.div {...reveal()}>
        <Eyebrow>{hi ? 'आपकी गोपनीयता' : 'Your privacy'}</Eyebrow>
        <h2 className="font-['Fraunces'] text-4xl sm:text-5xl text-[#282622] mb-6 leading-tight">{hi ? 'आपका विश्वास, हमारी ज़िम्मेदारी' : 'Your trust, our sacred duty'}</h2>
        <p className="text-[#6B6560] mb-10 max-w-md">{hi ? 'आपकी जन्म जानकारी सुरक्षित रहती है। हम आपका डेटा कभी किसी के साथ साझा नहीं करते।' : 'Your birth details stay protected. We never share your data with anyone, ever.'}</p>
        <div className="space-y-4">
          {[
            { icon: Lock, t: hi ? 'बैंक-स्तरीय एन्क्रिप्शन' : 'Bank-level encryption' },
            { icon: ShieldCheck, t: hi ? 'कोई डेटा शेयरिंग नहीं' : 'Zero data sharing' },
            { icon: Trash2, t: hi ? 'कभी भी डेटा डिलीट करें' : 'Delete anytime' },
          ].map((r, i) => (
            <motion.div key={r.t} {...reveal(i * 0.08)} className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-2xl bg-[#287A60]/10 flex items-center justify-center"><r.icon className="w-5 h-5 text-[#287A60]" /></span>
              <span className="font-medium text-[#282622]">{r.t}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
      <motion.div {...reveal(0.15)} className="relative flex items-center justify-center h-80">
        <motion.div className="absolute w-72 h-72 rounded-full border border-[#287A60]/20" animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 4, repeat: Infinity }} />
        <div className="absolute w-56 h-56 rounded-full bg-[#287A60]/5" />
        <div className="relative w-40 h-40 rounded-full bg-white shadow-[0_20px_60px_rgba(40,122,96,0.2)] flex items-center justify-center">
          <ShieldCheck className="w-20 h-20 text-[#287A60]" strokeWidth={1.4} />
        </div>
      </motion.div>
    </div>
  </section>
);

const FinalCTA = ({ hi, onStart }) => (
  <section className="px-5 sm:px-8 pb-24">
    <motion.div {...reveal()} className="relative max-w-6xl mx-auto rounded-[36px] overflow-hidden min-h-[380px] flex items-center" data-testid="landing-final-cta">
      <img src={CTA_IMAGE} alt="Rows of glowing diyas" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(40,20,10,0.88) 0%, rgba(40,20,10,0.55) 55%, rgba(40,20,10,0.2) 100%)' }} />
      <div className="relative p-10 sm:p-16 max-w-xl">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#F5B942] mb-4">{hi ? 'अपनी यात्रा शुरू करें' : 'Begin your journey'}</p>
        <h2 className="font-['Fraunces'] text-4xl sm:text-5xl text-[#FFF9EF] leading-tight mb-5">{hi ? 'सितारे आपका इंतज़ार कर रहे हैं' : 'Light the lamp of your destiny'}</h2>
        <p className="text-[#FFF9EF]/80 mb-8">{hi ? 'आज ही मुफ्त में शुरू करें।' : 'Start free today and see what the grahas have written for you.'}</p>
        <button onClick={onStart} className="shubh-shimmer inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-[#282622] hover:-translate-y-0.5 transition-transform" style={{ background: 'linear-gradient(135deg, #FFD066, #F5B942)' }} data-testid="final-cta-btn">
          {hi ? 'मुफ्त में शुरू करें' : 'Start Free Reading'} <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  </section>
);

export const ShubhLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const hi = language === 'hi';
  const onStart = () => navigate(user ? '/dashboard' : '/login');

  return (
    <div className="min-h-screen overflow-x-hidden text-[#282622]" style={{ background: 'linear-gradient(180deg, #FFF9EF 0%, #FCE9D9 40%, #FFF9EF 75%)' }} data-testid="shubh-landing-page">
      <LandingNav hi={hi} onStart={onStart} toggleLanguage={toggleLanguage} />
      <Hero hi={hi} onStart={onStart} navigate={navigate} />
      <Marquee hi={hi} />
      <Stats hi={hi} />
      <Features hi={hi} onStart={onStart} />
      <HowItWorks hi={hi} />
      <Trust hi={hi} />
      <FinalCTA hi={hi} onStart={onStart} />
      <footer className="py-10 border-t border-[#282622]/10 px-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#8A8279]">
          <p>© 2026 Nakshatra • {hi ? 'भारत में प्रेम से बनाया' : 'Made with devotion in India'}</p>
          <Link to="/privacy-policy" className="hover:text-[#C84B32]" data-testid="landing-privacy-link">{hi ? 'गोपनीयता नीति' : 'Privacy Policy'}</Link>
        </div>
      </footer>
    </div>
  );
};

export default ShubhLandingPage;
